import { useEffect, useMemo, useState } from "react";
import { FileText, FileCheck2, TrendingUp, Boxes, BedDouble, Scissors } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import api, { peso, fmtErr, todayStr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const PIE_COLORS = ["#0E7C84", "#E84855", "#F4A261", "#2A9D8F", "#E9C46A", "#0052FF"];

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function Reports() {
  const [range, setRange] = useState(7);
  const [summary, setSummary] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [ops, setOps] = useState(null);
  const [zreadings, setZreadings] = useState([]);

  const from = daysAgo(range - 1);
  const to = todayStr();

  const load = () => {
    const params = { date_from: from, date_to: to };
    api.get("/reports/sales-summary", { params }).then((r) => setSummary(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/reports/inventory", { params }).then((r) => setInventory(r.data)).catch(() => {});
    api.get("/reports/operations", { params }).then((r) => setOps(r.data)).catch(() => {});
    api.get("/reports/z-readings").then((r) => setZreadings(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [range]);

  const genZ = async () => {
    if (!window.confirm("Generate Z-Reading? This closes the current sales period.")) return;
    try {
      const { data } = await api.post("/reports/z-reading");
      toast.success(`${data.z_number} generated — ${peso(data.gross_sales)} for the period`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const dayData = useMemo(() => Object.entries(summary?.by_day || {}).map(([d, v]) => ({ day: d.slice(5), sales: v })), [summary]);
  const typeData = useMemo(() => Object.entries(summary?.by_type || {}).map(([k, v]) => ({ name: k, value: v })), [summary]);
  const payData = useMemo(() => Object.entries(summary?.by_payment || {}).map(([k, v]) => ({ name: k.toUpperCase(), value: v })), [summary]);

  return (
    <div className="animate-rise space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="reports-title">Reports &amp; Analytics</h1>
          <p className="text-sm text-slate-500">Sales, BIR readings, occupancy and profitability.</p>
        </div>
        <div className="flex gap-1.5">
          {[["1", 1], ["7D", 7], ["30D", 30]].map(([l, v]) => (
            <button key={v} onClick={() => setRange(v)} data-testid={`range-${v}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${range === v ? "bg-[#0E7C84] text-white" : "bg-white border border-[#BFE8E9]"}`}>
              {l === "1" ? "Today" : l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-[#0E7C84] text-white rounded-2xl p-5" data-testid="rep-gross">
          <TrendingUp className="w-5 h-5 text-[#F4A261] mb-2" />
          <p className="font-display font-bold text-2xl">{summary ? peso(summary.gross_sales) : "—"}</p>
          <p className="text-xs text-emerald-100/70">Gross sales ({summary?.transactions || 0} txn)</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="rep-vat">
          <FileText className="w-5 h-5 text-[#E84855] mb-2" />
          <p className="font-display font-bold text-2xl">{summary ? peso(summary.vat_collected) : "—"}</p>
          <p className="text-xs text-slate-500">VAT collected · {peso(summary?.vat_exempt_sales || 0)} exempt</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="rep-occupancy">
          <BedDouble className="w-5 h-5 text-[#2A9D8F] mb-2" />
          <p className="font-display font-bold text-2xl">{ops ? `${ops.boarding.occupancy_rate}%` : "—"}</p>
          <p className="text-xs text-slate-500">Boarding occupancy ({ops?.boarding.occupied_nights || 0}/{ops?.boarding.capacity_nights || 0} room-nights)</p>
        </div>
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="rep-profit">
          <Boxes className="w-5 h-5 text-[#F4A261] mb-2" />
          <p className="font-display font-bold text-2xl">{inventory ? peso(inventory.gross_profit) : "—"}</p>
          <p className="text-xs text-slate-500">Retail gross profit (COGS {peso(inventory?.cogs || 0)})</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5 lg:col-span-2">
          <h3 className="font-display font-semibold mb-4">Daily sales</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dayData}>
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => peso(v)} />
              <Bar dataKey="sales" fill="#0E7C84" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5">
          <h3 className="font-display font-semibold mb-4">Payment methods</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={payData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={3}>
                {payData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => peso(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5">
          <h3 className="font-display font-semibold mb-4">Revenue by segment</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={typeData} dataKey="value" nameKey="name" outerRadius={70} paddingAngle={3}>
                {typeData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => peso(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="groomer-utilization">
          <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Scissors className="w-4 h-4 text-[#E84855]" /> Groomer productivity</h3>
          <div className="space-y-2 text-sm">
            {Object.entries(ops?.groomers || {}).map(([name, s]) => (
              <div key={name} className="flex justify-between border-b border-[#E2F5F5] pb-1.5">
                <span>{name} <span className="text-xs text-slate-400">({s.services} svc)</span></span>
                <span className="text-right">{peso(s.revenue)}<br /><span className="text-xs text-[#0E7C84]">comm {peso(s.commission)}</span></span>
              </div>
            ))}
            {Object.keys(ops?.groomers || {}).length === 0 && <p className="text-slate-400">No completed services in range.</p>}
          </div>
          <p className="text-xs text-slate-500 mt-3">Customer retention: <strong>{ops?.customers.retention_rate ?? 0}%</strong> ({ops?.customers.repeat || 0} repeat of {ops?.customers.unique || 0})</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="bir-panel">
          <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-[#0E7C84]" /> BIR Readings</h3>
          <Button className="w-full bg-[#0E7C84] hover:bg-[#093F46] mb-3" onClick={genZ} data-testid="gen-z-reading-btn">Generate Z-Reading (end of day)</Button>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {zreadings.map((z) => (
              <div key={z.id} className="flex justify-between text-xs border-b border-[#E2F5F5] pb-1.5" data-testid={`z-${z.z_number}`}>
                <span className="font-mono font-semibold">{z.z_number}</span>
                <span>{z.period_to?.slice(0, 10)}</span>
                <span className="font-bold">{peso(z.gross_sales)}</span>
              </div>
            ))}
            {zreadings.length === 0 && <p className="text-xs text-slate-400">No Z-readings yet. X-Reading data is live on the dashboard.</p>}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold">Inventory &amp; profitability</h3>
          <p className="text-xs text-slate-500">Stock value: <strong>{peso(inventory?.stock_value || 0)}</strong> · Deadstock: <strong className="text-red-600">{inventory?.deadstock.length || 0}</strong> SKUs</p>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead><TableHead>Product</TableHead><TableHead className="text-right">Sold</TableHead>
                <TableHead className="text-right">Revenue</TableHead><TableHead className="text-right">COGS</TableHead>
                <TableHead className="text-right">Margin</TableHead><TableHead className="text-right">Stock</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(inventory?.products || []).map((p) => (
                <TableRow key={p.id} data-testid={`rep-product-${p.sku}`}>
                  <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                  <TableCell className="text-sm">
                    {p.name} {inventory?.deadstock.find((d) => d.id === p.id) && <Badge className="ml-1 bg-red-100 text-red-600 hover:bg-red-100 text-[9px]">deadstock</Badge>}
                  </TableCell>
                  <TableCell className="text-right">{p.sold_qty}</TableCell>
                  <TableCell className="text-right">{peso(p.revenue)}</TableCell>
                  <TableCell className="text-right text-slate-500">{peso(p.cogs)}</TableCell>
                  <TableCell className="text-right font-semibold text-[#0E7C84]">{p.margin_pct}%</TableCell>
                  <TableCell className="text-right">{p.stock}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
